import { GenNode } from '../types/carbon/models/GenNode'
import { getNodeID } from './nodeTypes'

/**
 * Filter syntax follows the pattern: variable_name(codes) where codes can be:
 * - Single codes: variable_name(1)
 * - Multiple codes: variable_name(1;2;3)
 * - Ranges: variable_name(1/5) for codes 1,2,3,4,5
 * - Mixed: variable_name(1;3/5;7)
 * - All codes: variable_name(*)
 * - Complex filters: variable1(1;2) && variable2(*) && variable3(3/5)
 */

export interface FilterExpression {
  variable: string
  codes: string[]
  useAllCodes: boolean
}

/**
 * Parses a filter string into filter expressions
 * Example: "bb_master(*) && bb_age(1/2)" -> [
 *   { variable: "bb_master", codes: [], useAllCodes: true },
 *   { variable: "bb_age", codes: ["1", "2"], useAllCodes: false }
 * ]
 */
export function parseFilterString(filterString: string): FilterExpression[] {
  if (!filterString || !filterString.trim()) {
    return []
  }

  const expressions: FilterExpression[] = []
  
  // Split by && to get individual variable expressions
  const parts = filterString.split('&&').map(part => part.trim())
  
  for (const part of parts) {
    const match = part.match(/^(\w+)\(([^)]+)\)$/)
    if (!match) continue
    
    const [, variable, codesStr] = match
    
    if (codesStr === '*') {
      expressions.push({
        variable,
        codes: [],
        useAllCodes: true
      })
    } else {
      const codes = expandCodeString(codesStr)
      expressions.push({
        variable,
        codes,
        useAllCodes: false
      })
    }
  }
  
  return expressions
}

/**
 * Expands a code string like "1;3/5;7" into individual codes ["1", "3", "4", "5", "7"]
 */
export function expandCodeString(codesStr: string): string[] {
  const codes: string[] = []
  const parts = codesStr.split(';')
  
  for (const part of parts) {
    const trimmed = part.trim()
    if (trimmed.includes('/')) {
      // Handle range like "1/5"
      const [start, end] = trimmed.split('/').map(s => parseInt(s.trim(), 10))
      if (!isNaN(start) && !isNaN(end)) {
        for (let i = start; i <= end; i++) {
          codes.push(i.toString())
        }
      }
    } else {
      // Handle single code
      codes.push(trimmed)
    }
  }
  
  return codes
}

/**
 * Compresses an array of codes into the most compact string representation
 * Example: ["1", "2", "3", "5", "7", "8", "9"] -> "1/3;5;7/9"
 */
export function compressCodeArray(codes: string[]): string {
  if (codes.length === 0) return ''
  
  // Convert to numbers and sort, keeping track of original strings for non-numeric codes
  const numericCodes = codes
    .map(code => ({ original: code, numeric: parseInt(code, 10) }))
    .filter(item => !isNaN(item.numeric))
    .sort((a, b) => a.numeric - b.numeric)
  
  const nonNumericCodes = codes.filter(code => isNaN(parseInt(code, 10)))
  
  // Group consecutive numeric codes into ranges
  const ranges: string[] = []
  let i = 0
  
  while (i < numericCodes.length) {
    const start = numericCodes[i].numeric
    let end = start
    let j = i
    
    // Find consecutive numbers
    while (j + 1 < numericCodes.length && numericCodes[j + 1].numeric === end + 1) {
      end = numericCodes[j + 1].numeric
      j++
    }
    
    // Add range or single number
    if (start === end) {
      ranges.push(start.toString())
    } else {
      ranges.push(`${start}/${end}`)
    }
    
    i = j + 1
  }
  
  // Add non-numeric codes at the end
  ranges.push(...nonNumericCodes)
  
  return ranges.join(';')
}

/**
 * Creates a filter string from selectedVariables structure
 */
export function createFilterFromSelectedVariables(selectedVariables: GenNode[], nodeMap: Record<string, GenNode>, allowEmptyVariables = false): string {
  if (selectedVariables.length === 0) return ''
  
  const expressions: string[] = []
  
  for (const selectedVar of selectedVariables) {
    const varName = selectedVar.value1 || ''
    const fullVariableNode = nodeMap[getNodeID(selectedVar)]
    const selectedCodes = selectedVar.children || []
    const allCodes = fullVariableNode?.children || []

    if(allowEmptyVariables && allCodes.filter(code => code.type === 'Code').length === 0) {
      expressions.push(`${varName}(*)`)
      continue
    }
    
    if (selectedCodes.length === 0) continue
    
    // If all codes are selected, use asterisk
    if (allCodes.length > 0 && selectedCodes.length === allCodes.length) {
      expressions.push(`${varName}(*)`)
    } else {
      // Get the actual code values (value1 from the GenNode)
      const codeValues = selectedCodes
        .map(selectedCode => {
          const codeValue = selectedCode.value1
          if (!codeValue) {
            console.error('Code node missing value1:', selectedCode)
            return null
          }
          // Validate it parses as a number (required for range compression)
          const numValue = parseInt(codeValue, 10)
          if (isNaN(numValue)) {
            console.error('Code value1 is not numeric:', codeValue)
            return null
          }
          return codeValue
        })
        .filter((val): val is string => val !== null)

      if (codeValues.length > 0) {
        const compressedCodes = compressCodeArray(codeValues)
        expressions.push(`${varName}(${compressedCodes})`)
      }
    }
  }

  return expressions.join('&')
}

/**
 * AND two Carbon filter expressions. Carbon has no `AND` keyword: `&` is AND
 * and `|` is OR, and `&` binds tighter than `|` (docs/carbon.md §5), so
 * `a|b` & `c` written bare runs as `a|(b&c)`. Each side is bracketed before
 * joining, `(a)&(b)`, the form goapi's ApplyOverrides CombineFilter writes
 * (#1005). An empty side is dropped, never `()&(b)`.
 */
export function andFilters(a?: string, b?: string): string {
  const left = (a || '').trim()
  const right = (b || '').trim()
  if (left && right) return `(${left})&(${right})`
  return left || right
}

/**
 * A spec with `extra` ANDed onto its own filter, as the server does for a
 * saved table (CombineFilter): a filter the spec has switched off
 * (useFilter false) is not brought back, so `extra` then stands alone.
 */
export function withAndedFilter<T extends { filter?: string; useFilter?: boolean }>(spec: T, extra?: string): T {
  if (!(extra || '').trim()) return spec
  const own = spec.useFilter === false ? '' : spec.filter
  return { ...spec, filter: andFilters(own, extra), useFilter: true }
}
